import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import ScreenContainer from '../components/ScreenContainer';
import CategorySpendingChart, { CategorySpendDataPoint } from '../components/CategorySpendingChart';
import CurrencyDisplay from '../components/AmountDisplay';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchTransactionRange } from '../store/slices/transactionsSlice';
import { fetchTags, selectTagMap } from '../store/slices/tagsSlice';
import { Tag, Transaction } from '../types';
import { useNavigation } from '../hooks/useNavigation';
import { FiChevronRight } from 'react-icons/fi';
import { findTagByName, getDescendantTagNames, normalizeTagKey, normalizeTagName, UNTAGGED_KEY } from '../utils/tagHelpers';
import { isInvestmentDebitTransaction } from '../utils/cashFlowUtils';
import { getCurrentKolkataYearMonth, parseTransactionDateParts } from '../utils/dateTimeUtils';
import { getRenderableTransactionRoots } from '../utils/transactionUtils';

interface CategorySpendingRouteState {
  tagName?: string;
  initialYear?: number;
  initialMonth?: number;
  initialGranularity?: SpendGranularity;
}

type SpendGranularity = 'monthly' | 'yearly';

interface CategoryAggregation {
  monthTotals: Map<string, number>;
  yearTotals: Map<number, number>;
  transactionsByMonth: Map<string, Transaction[]>;
  transactionsByYear: Map<number, Transaction[]>;
  firstTransactionPeriod: { year: number; month: number } | null;
  lastTransactionPeriod: { year: number; month: number } | null;
}

interface ChildTagSpending {
  key: string;
  name: string;
  amount: number;
  percentage: number;
  tagName?: string;
  isOther?: boolean;
}

const getMonthAbbr = (month: number): string => {
  return new Date(2000, month - 1, 1).toLocaleString('default', { month: 'short' });
};

const getMonthName = (month: number): string => {
  return new Date(2000, month - 1, 1).toLocaleString('default', { month: 'long' });
};

const HISTORICAL_FETCH_START_YEAR = 1970;
const CHILD_TAG_RING_SIZE = 48;
const CHILD_TAG_RING_STROKE = 8;
const CHILD_TAG_RING_RADIUS = (CHILD_TAG_RING_SIZE - CHILD_TAG_RING_STROKE) / 2;
const CHILD_TAG_RING_CIRCUMFERENCE = 2 * Math.PI * CHILD_TAG_RING_RADIUS;

const CategorySpendingDetailsScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navigateTo } = useNavigation();
  const { tagName } = useParams<{ tagName: string }>();
  const location = useLocation();
  const routeState = location.state as CategorySpendingRouteState | null;

  const decodedTagName = decodeURIComponent(tagName ?? '').trim();
  const isTagNameValid = decodedTagName.length > 0;

  const { year: currentYear, month: fallbackMonth } = getCurrentKolkataYearMonth();
  const initialYear =
    routeState?.initialYear && Number.isFinite(routeState.initialYear)
      ? routeState.initialYear
      : currentYear;
  const initialMonth =
    routeState?.initialMonth && routeState.initialMonth >= 1 && routeState.initialMonth <= 12
      ? routeState.initialMonth
      : fallbackMonth;

  const [selectedYear, setSelectedYear] = useState<number>(initialYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(initialMonth);
  const [granularity, setGranularity] = useState<SpendGranularity>(routeState?.initialGranularity ?? 'monthly');
  const [chartRecenterToken, setChartRecenterToken] = useState<number>(0);
  const hasRequestedInitialRangeRef = useRef(false);

  const { transactions, status: transactionsStatus } = useAppSelector((state) => state.transactions);
  const { tags, status: tagsStatus } = useAppSelector((state) => state.tags);
  const tagMap = useAppSelector(selectTagMap);
  const deferredTransactions = useDeferredValue(transactions);
  const deferredTags = useDeferredValue(tags);
  const deferredTagMap = useDeferredValue(tagMap);

  useEffect(() => {
    if (tagsStatus === 'idle') {
      dispatch(fetchTags());
    }
  }, [dispatch, tagsStatus]);

  const fullHistoryRange = useMemo(() => ({
    startYear: HISTORICAL_FETCH_START_YEAR,
    startMonth: 1,
    endYear: currentYear,
    endMonth: 12,
  }), [currentYear]);

  useEffect(() => {
    if (!isTagNameValid) {
      return;
    }

    if (hasRequestedInitialRangeRef.current) {
      return;
    }

    if (transactionsStatus === 'loading' || transactionsStatus === 'loadingMore' || transactionsStatus === 'failed') {
      return;
    }

    hasRequestedInitialRangeRef.current = true;
    dispatch(fetchTransactionRange(fullHistoryRange));
  }, [dispatch, fullHistoryRange, isTagNameValid, transactionsStatus]);

  const resolvedTagName = useMemo(() => {
    if (!isTagNameValid) {
      return 'Category';
    }
    if (normalizeTagName(decodedTagName) === 'untagged') {
      return 'Untagged';
    }
    const fromTags = deferredTags.find((tag) => normalizeTagName(tag.name) === normalizeTagName(decodedTagName));
    return fromTags?.name ?? routeState?.tagName ?? decodedTagName;
  }, [decodedTagName, deferredTags, isTagNameValid, routeState?.tagName]);

  useEffect(() => {
    if (!isTagNameValid) {
      return;
    }

    const currentHistoryState = window.history.state;
    const currentRouteState = (currentHistoryState?.usr ?? {}) as CategorySpendingRouteState;
    const nextRouteState: CategorySpendingRouteState = {
      ...currentRouteState,
      tagName: resolvedTagName,
      initialYear: selectedYear,
      initialMonth: granularity === 'monthly' ? selectedMonth : undefined,
      initialGranularity: granularity,
    };

    window.history.replaceState(
      {
        ...currentHistoryState,
        usr: nextRouteState,
      },
      '',
    );
  }, [granularity, isTagNameValid, resolvedTagName, selectedMonth, selectedYear]);

  const relevantTagNames = useMemo(() => {
    if (!isTagNameValid) {
      return new Set<string>();
    }
    if (normalizeTagName(decodedTagName) === 'untagged') {
      return new Set<string>([UNTAGGED_KEY]);
    }
    return getDescendantTagNames(deferredTags, decodedTagName);
  }, [decodedTagName, deferredTags, isTagNameValid]);

  const relevantCategoryTransactions = useMemo(() => {
    if (relevantTagNames.size === 0) {
      return [] as Transaction[];
    }

    return deferredTransactions.filter((transaction) => {
      if (transaction.type !== 'DEBIT' || transaction.excludeFromAccounting || isInvestmentDebitTransaction(transaction, deferredTagMap)) {
        return false;
      }

      const transactionTagKey = normalizeTagKey(transaction.tag);
      return relevantTagNames.has(transactionTagKey);
    });
  }, [deferredTagMap, deferredTransactions, relevantTagNames]);

  const selectedTag = useMemo<Tag | null>(() => {
    if (!isTagNameValid || normalizeTagName(decodedTagName) === 'untagged') {
      return null;
    }

    return findTagByName(deferredTags, decodedTagName);
  }, [decodedTagName, deferredTags, isTagNameValid]);

  const categoryAggregation = useMemo<CategoryAggregation>(() => {
    const monthTotals = new Map<string, number>();
    const yearTotals = new Map<number, number>();
    const transactionsByMonth = new Map<string, Transaction[]>();
    const transactionsByYear = new Map<number, Transaction[]>();
    let firstTransactionPeriod: { year: number; month: number } | null = null;
    let lastTransactionPeriod: { year: number; month: number } | null = null;

    relevantCategoryTransactions.forEach((transaction) => {
      const transactionDate = parseTransactionDateParts(transaction.transactionDate);
      const transactionYear = transactionDate.year;
      const transactionMonth = transactionDate.month;
      const monthId = `${transactionYear}-${transactionMonth}`;
      const transactionMonthIndex = transactionYear * 12 + transactionMonth;

      monthTotals.set(monthId, (monthTotals.get(monthId) ?? 0) + transaction.amount);
      yearTotals.set(transactionYear, (yearTotals.get(transactionYear) ?? 0) + transaction.amount);

      const monthTransactions = transactionsByMonth.get(monthId) ?? [];
      monthTransactions.push(transaction);
      transactionsByMonth.set(monthId, monthTransactions);

      const yearTransactions = transactionsByYear.get(transactionYear) ?? [];
      yearTransactions.push(transaction);
      transactionsByYear.set(transactionYear, yearTransactions);

      if (
        firstTransactionPeriod == null ||
        transactionMonthIndex < firstTransactionPeriod.year * 12 + firstTransactionPeriod.month
      ) {
        firstTransactionPeriod = { year: transactionYear, month: transactionMonth };
      }
      if (
        lastTransactionPeriod == null ||
        transactionMonthIndex > lastTransactionPeriod.year * 12 + lastTransactionPeriod.month
      ) {
        lastTransactionPeriod = { year: transactionYear, month: transactionMonth };
      }
    });

    return {
      monthTotals,
      yearTotals,
      transactionsByMonth,
      transactionsByYear,
      firstTransactionPeriod,
      lastTransactionPeriod,
    };
  }, [relevantCategoryTransactions]);

  const monthlyChartData = useMemo((): CategorySpendDataPoint[] => {
    const { firstTransactionPeriod, lastTransactionPeriod, monthTotals: totalsByMonth } = categoryAggregation;

    if (firstTransactionPeriod == null || lastTransactionPeriod == null) {
      return [];
    }

    const monthTotals: CategorySpendDataPoint[] = [];
    let year = firstTransactionPeriod.year;
    let month = firstTransactionPeriod.month;
    while (year < lastTransactionPeriod.year || (year === lastTransactionPeriod.year && month <= lastTransactionPeriod.month)) {
      const pointId = `${year}-${month}`;
      monthTotals.push({
        id: pointId,
        label: getMonthAbbr(month),
        year,
        month,
        amount: totalsByMonth.get(pointId) ?? 0,
      });
      month += 1;
      if (month > 12) {
        month = 1;
        year += 1;
      }
    }

    return monthTotals;
  }, [categoryAggregation]);

  const yearlyChartData = useMemo((): CategorySpendDataPoint[] => {
    const { firstTransactionPeriod, lastTransactionPeriod, yearTotals } = categoryAggregation;

    if (firstTransactionPeriod == null || lastTransactionPeriod == null) {
      return [];
    }

    const firstYear = firstTransactionPeriod.year;
    const lastYear = lastTransactionPeriod.year;

    const chartYears: number[] = [];
    for (let year = firstYear; year <= lastYear; year += 1) {
      chartYears.push(year);
    }

    return chartYears.map((year) => ({
      id: `${year}`,
      label: `${year}`,
      year,
      amount: yearTotals.get(year) ?? 0,
    }));
  }, [categoryAggregation]);

  const chartData = granularity === 'yearly' ? yearlyChartData : monthlyChartData;

  const selectedPointId = granularity === 'yearly' ? `${selectedYear}` : `${selectedYear}-${selectedMonth}`;
  const selectedPoint = useMemo(
    () => chartData.find((point) => point.id === selectedPointId),
    [chartData, selectedPointId]
  );
  const selectedPeriodSpend = selectedPoint?.amount ?? 0;

  useEffect(() => {
    if (chartData.length === 0) {
      return;
    }

    const lastPoint = chartData[chartData.length - 1];
    const selectedExists = chartData.some((point) => point.id === selectedPointId);
    if (selectedExists) {
      return;
    }

    if (lastPoint.year !== selectedYear) {
      setSelectedYear(lastPoint.year);
    }
    if (granularity === 'monthly' && lastPoint.month && lastPoint.month !== selectedMonth) {
      setSelectedMonth(lastPoint.month);
    }
    setChartRecenterToken((token) => token + 1);
  }, [chartData, granularity, selectedMonth, selectedPointId, selectedYear]);

  const transactionsForSelectedPeriod = useMemo(() => {
    if (granularity === 'yearly') {
      return categoryAggregation.transactionsByYear.get(selectedYear) ?? [];
    }

    const monthKey = `${selectedYear}-${selectedMonth}`;
    return categoryAggregation.transactionsByMonth.get(monthKey) ?? [];
  }, [categoryAggregation.transactionsByMonth, categoryAggregation.transactionsByYear, granularity, selectedMonth, selectedYear]);

  const visibleTransactionCount = useMemo(() => {
    return getRenderableTransactionRoots(transactionsForSelectedPeriod).length;
  }, [transactionsForSelectedPeriod]);

  const childTagSpending = useMemo<ChildTagSpending[]>(() => {
    const children = selectedTag?.children ?? [];
    if (children.length === 0) {
      return [];
    }

    const childItems: ChildTagSpending[] = children
      .map((child) => {
        const childTagNames = getDescendantTagNames(deferredTags, child.name);
        const amount = transactionsForSelectedPeriod.reduce((sum, transaction) => {
          return childTagNames.has(normalizeTagKey(transaction.tag)) ? sum + transaction.amount : sum;
        }, 0);

        return {
          key: normalizeTagName(child.name),
          name: child.name,
          amount,
          percentage: selectedPeriodSpend > 0 ? (amount / selectedPeriodSpend) * 100 : 0,
          tagName: child.name,
        };
      })
      .filter((item) => item.amount > 0);

    const childTotal = childItems.reduce((sum, item) => sum + item.amount, 0);
    const directParentSpend = Math.max(0, selectedPeriodSpend - childTotal);
    if (directParentSpend > 0) {
      childItems.push({
        key: `${normalizeTagName(resolvedTagName)}-direct`,
        name: 'Other',
        amount: directParentSpend,
        percentage: selectedPeriodSpend > 0 ? (directParentSpend / selectedPeriodSpend) * 100 : 0,
        isOther: true,
      });
    }

    childItems.sort((a, b) => {
      if (b.amount !== a.amount) {
        return b.amount - a.amount;
      }

      return a.name.localeCompare(b.name);
    });

    return childItems;
  }, [deferredTags, resolvedTagName, selectedPeriodSpend, selectedTag?.children, transactionsForSelectedPeriod]);

  const hasChartData = chartData.some((dataPoint) => dataPoint.amount > 0);
  const isChartLoading =
    (transactionsStatus === 'loading' ||
      transactionsStatus === 'loadingMore' ||
      transactionsStatus === 'idle') &&
    !hasChartData;

  const handlePointSelect = useCallback((pointId: string) => {
    const selectedDataPoint = chartData.find((point) => point.id === pointId);
    if (!selectedDataPoint) {
      return;
    }

    if (selectedDataPoint.year !== selectedYear) {
      setSelectedYear(selectedDataPoint.year);
    }

    if (granularity === 'monthly' && selectedDataPoint.month && selectedDataPoint.month !== selectedMonth) {
      setSelectedMonth(selectedDataPoint.month);
    }
  }, [chartData, granularity, selectedMonth, selectedYear]);

  const handleGranularityChange = useCallback((nextGranularity: SpendGranularity) => {
    const now = getCurrentKolkataYearMonth();
    setGranularity(nextGranularity);
    setSelectedYear(now.year);
    if (nextGranularity === 'monthly') {
      setSelectedMonth(now.month);
    }
    setChartRecenterToken((token) => token + 1);
  }, []);

  const handleViewAllTransactions = useCallback(() => {
    navigateTo(`/cash-flow/transactions/all/${selectedYear}/${selectedMonth}`, {
      state: {
        categoryTagName: resolvedTagName,
        periodScope: granularity === 'yearly' ? 'year' : 'month',
      },
    });
  }, [granularity, navigateTo, resolvedTagName, selectedMonth, selectedYear]);

  const handleChildTagClick = useCallback((childTagName: string) => {
    navigateTo(`/spending-summary/category/${encodeURIComponent(childTagName)}`, {
      state: {
        tagName: childTagName,
        initialYear: selectedYear,
        initialMonth: granularity === 'monthly' ? selectedMonth : undefined,
        initialGranularity: granularity,
      },
    });
  }, [granularity, navigateTo, selectedMonth, selectedYear]);

  const activeMonthYearLabel = granularity === 'yearly' ? `Year ${selectedYear}` : `${getMonthName(selectedMonth)} ${selectedYear}`;

  const listTopContent = (
    <>
      <div className="w-full px-4">
        <div className="mx-auto w-full max-w-sm pt-1">
          <div className="inline-flex rounded-2xl bg-muted/70 p-1">
            <button
              className={`px-4 py-2 rounded-xl text-sm font-semibold ${granularity === 'monthly'
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground'
                }`}
              type="button"
              onClick={() => handleGranularityChange('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-4 py-2 rounded-xl text-sm font-semibold ${granularity === 'yearly'
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground'
                }`}
              type="button"
              onClick={() => handleGranularityChange('yearly')}
            >
              Yearly
            </button>
          </div>
        </div>
      </div>

      <div className="w-full pt-6">
        <div className="relative">
          <div className="absolute left-1/2 -translate-x-1/2 rounded-[16px] border border-border bg-muted p-[4px] z-[2] shadow-sm">
            <div className="rounded-[12px] border border-primary/30 bg-background px-[16px] py-[8px] text-center">
              <CurrencyDisplay
                amount={selectedPeriodSpend}
                className="text-xl font-semibold justify-center tabular-nums"
                type="DEBIT"
                showType={true}
                showFraction={false}
                smallRupeeSymbol={true}
              />
            </div>
          </div>

          <div className="absolute top-[53px] h-[16px] left-1/2 transform -translate-x-1/2 w-4 rounded-full border-4 border-background bg-muted-foreground z-[3] pointer-events-none"></div>
          <div className="absolute top-[60px] bottom-0 mb-[34px] left-1/2 transform -translate-x-1/2 w-[2px] bg-muted-foreground z-[4] pointer-events-none"></div>

          <div className="pt-[64px] pb-[8px]">
            <CategorySpendingChart
              data={chartData}
              selectedPointId={selectedPointId}
              isLoading={isChartLoading}
              onPointSelect={handlePointSelect}
              emptyMessage={granularity === 'yearly' ? 'No category spend data available.' : 'No category spend data for this year.'}
              recenterToken={chartRecenterToken}
            />
          </div>
        </div>
      </div>

      {childTagSpending.length > 0 && (
        <div className="w-full pt-2">
          <div className="mx-auto flex w-full max-w-sm gap-4 overflow-x-auto px-4 pb-1">
            {childTagSpending.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => item.tagName && handleChildTagClick(item.tagName)}
                disabled={!item.tagName}
                className={`flex flex-none items-center gap-2 py-2 text-left ${item.tagName ? '' : 'cursor-default opacity-70'}`}
              >
                <span className="relative grid h-12 w-12 flex-shrink-0 place-items-center" aria-hidden="true">
                  <svg
                    className="-rotate-90"
                    width={CHILD_TAG_RING_SIZE}
                    height={CHILD_TAG_RING_SIZE}
                    viewBox={`0 0 ${CHILD_TAG_RING_SIZE} ${CHILD_TAG_RING_SIZE}`}
                  >
                    <circle
                      cx={CHILD_TAG_RING_SIZE / 2}
                      cy={CHILD_TAG_RING_SIZE / 2}
                      r={CHILD_TAG_RING_RADIUS}
                      fill="none"
                      stroke="rgb(var(--muted))"
                      strokeWidth={CHILD_TAG_RING_STROKE}
                    />
                    <circle
                      cx={CHILD_TAG_RING_SIZE / 2}
                      cy={CHILD_TAG_RING_SIZE / 2}
                      r={CHILD_TAG_RING_RADIUS}
                      fill="none"
                      stroke={`rgb(var(${item.isOther ? '--muted-foreground' : '--primary'}))`}
                      strokeWidth={CHILD_TAG_RING_STROKE}
                      strokeLinecap="round"
                      strokeDasharray={`${Math.min(item.percentage, 100) / 100 * CHILD_TAG_RING_CIRCUMFERENCE} ${CHILD_TAG_RING_CIRCUMFERENCE}`}
                    />
                  </svg>
                  <span className={`absolute inset-1.5 grid place-items-center rounded-full bg-background text-sm font-bold ${item.isOther ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {item.name.slice(0, 1).toUpperCase()}
                  </span>
                </span>
                <span>
                  <span className={`block whitespace-nowrap text-sm font-medium leading-5 ${item.isOther ? 'text-muted-foreground/80' : 'text-muted-foreground'}`}>{item.name}</span>
                  <CurrencyDisplay
                    amount={item.amount}
                    className={`mt-0.5 whitespace-nowrap text-lg font-semibold leading-6 ${item.isOther ? 'text-muted-foreground' : 'text-foreground'}`}
                    valueClassName={`${item.isOther ? 'text-muted-foreground' : 'text-foreground'} tabular-nums`}
                    symbolClassName="text-muted-foreground"
                    showType={false}
                    showFraction={false}
                  />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="w-full pt-2">
        <div className="w-full px-4">
          <div className="mx-auto w-full max-w-sm">
            <div className="flex justify-between items-center border-b border-border pb-3 text-sm">
              <span className="font-semibold text-foreground">
                {granularity === 'yearly' ? `${selectedYear}` : `${getMonthName(selectedMonth)} ${selectedYear}`}
              </span>
              <span className="text-muted-foreground">
                {visibleTransactionCount} transaction{visibleTransactionCount === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {transactionsForSelectedPeriod.length > 0 && (
        <div className="w-full px-4 pt-3">
          <div className="mx-auto w-full max-w-sm">
            <button
              type="button"
              onClick={handleViewAllTransactions}
              className="flex justify-between items-center max-w-sm w-full p-3 bg-card rounded-lg border border-border bg-muted hover:bg-muted/80"
            >
              <div>
                <p className="text-base font-semibold text-foreground text-left">View Transactions</p>
                <p className="text-sm text-left text-muted-foreground">{activeMonthYearLabel}</p>
              </div>
              <FiChevronRight size={20} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      )}

      {transactionsForSelectedPeriod.length === 0 && (
        <div className="py-4 px-2 text-center text-sm text-muted-foreground">
          No transactions found for this {granularity === 'yearly' ? 'year' : 'month'}.
        </div>
      )}
    </>
  );

  if (!isTagNameValid) {
    return (
      <ScreenContainer title="Category">
        <div className="w-full max-w-sm px-6 pt-12 text-center text-muted-foreground">
          Invalid category selected.
        </div>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer title={resolvedTagName}>
      <div className="flex h-full flex-col w-full min-h-0">
        <div className="w-full flex-1 min-h-0">
          {listTopContent}
        </div>
      </div>
    </ScreenContainer>
  );
};

export default CategorySpendingDetailsScreen;
